import _ from 'underscore';

import * as benchlingUtil from 'BenchlingUtil';

// Keep in line with benchling/pybio/proteins/isoelectric_point.py
const POSITIVE_AMINO_ACIDS = ['K', 'R', 'H'];
const GENERIC_NTERM_PKA = 7.5;
const GENERIC_CTERM_PKA = 3.55;
const NTERM_PKAS: {[aminoAcid: string]: number} = {
  A: 7.59,
  M: 7.0,
  S: 6.93,
  P: 8.36,
  T: 6.82,
  V: 7.44,
  E: 7.7,
};
const CTERM_PKAS: {[aminoAcid: string]: number} = {
  D: 4.55,
  E: 4.75,
};
const PKA_VALUES: {[aminoAcid: string]: number} = {
  K: 10.0,
  R: 12.0,
  H: 5.98,

  D: 4.05,
  E: 4.45,
  C: 9.0,
  Y: 10.0,
};

// Utility method for computing charge for a given
// Note: fractionProtonated / deprotonated is the same as partial charge
const pKaToPartialCharge = ({
  pKa,
  isPositive,
  pH,
}: {
  pKa: number;
  isPositive: boolean;
  pH: number;
}): number => {
  // If there's no pKa, it's not a charged AA, so return 0
  if (pKa == null) {
    return 0;
  }

  // Ratio of deprotonated amino acid to protonated amino acid: e.g., ([A-] / [HA]) or  ([B] / [BH+])
  const deprotonatedOdds = Math.pow(10, pH - pKa);

  const fractionProtonated = 1 / (1 + deprotonatedOdds);
  const fractionDeprotonated = 1 - fractionProtonated;

  if (isPositive) {
    // If it's a positively charged AA, then its contribution to the charge is the fraction that is
    // *protonated*
    return fractionProtonated;
  } else {
    // Otherwise, it's a negatively charged AA, so its contribution of negative charge is the
    // fraction that is *deprotonated*
    return -fractionDeprotonated;
  }
};

const getSequencePartialCharges = (aminoAcids: string, pH: number): number => {
  const aaToPartialCharge = (aa: string): number =>
    pKaToPartialCharge({
      pKa: PKA_VALUES[aa],
      // eslint-disable-next-line no-restricted-syntax
      isPositive: _.contains(POSITIVE_AMINO_ACIDS, aa),
      pH,
    });
  return benchlingUtil.sum(aminoAcids, aaToPartialCharge);
};

const getTerminalPartialCharges = ({
  nTerm,
  cTerm,
  pH,
}: {
  nTerm: string;
  cTerm: string;
  pH: number;
}): number => {
  let charge = 0;
  // The N-terminus is always positive and C-terminus is always negative
  // Certain AAs have slightly different pKas when exposed at a terminus
  const nTermPKa = NTERM_PKAS[nTerm] || GENERIC_NTERM_PKA;
  const cTermPKa = CTERM_PKAS[cTerm] || GENERIC_CTERM_PKA;
  charge += pKaToPartialCharge({
    pKa: nTermPKa,
    isPositive: true,
    pH,
  });
  charge += pKaToPartialCharge({
    pKa: cTermPKa,
    isPositive: false,
    pH,
  });
  return charge;
};

/**
 * Use ProteinProperties.getChargeAtPH instead, this does not include residue validation.
 *
 * Returns calculated charge for protein with amino acids at a given buffer pH
 */
export default function _getChargeAtPH(aminoAcids: string, pH: number): number {
  let charge = 0;
  charge += getSequencePartialCharges(aminoAcids, pH);
  charge += getTerminalPartialCharges({
    nTerm: aminoAcids[0],
    cTerm: aminoAcids[aminoAcids.length - 1],
    pH,
  });
  return charge;
}
